import { validFiscalCode } from "./fiscalCode.js";

export { validFiscalCode };

export const BANK_HEADERS = [
  "ID Associazione",
  "Denominazione Cliente",
  "Codice Fiscale",
  "Codice ABI",
  "Codice CAB",
  "IBAN",
  "Cod. Conto AVERE - TeamSystem",
  "Cod. Conto AVERE - Datev Aziende",
  "Cod. Conto AVERE - Datev Lib. Prof.",
  "Cod. Conto AVERE - Zucchetti",
  "Cod. Conto AVERE - Seac",
  "Cod. Conto AVERE - Sistemi",
];

export const BANK_ACCOUNT_COLUMNS = BANK_HEADERS.filter((header) =>
  header.startsWith("Cod. Conto AVERE")
);

export const ARCHIVE_9001_HEADERS = [
  "Denominazione Cliente",
  "Codice Fiscale",
  "Importo DARE",
  "Importo AVERE",
  ...BANK_HEADERS.slice(6).flatMap((column) => [
    column.replace("Cod. Conto AVERE", "Descrizione Cod. Conto"),
    column.replace("AVERE", "DARE"),
    column,
  ]),
];

export function normalizeIban(value) {
  return String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

export function formatItalianIban(value) {
  const iban = normalizeIban(value).slice(0, 27);
  if (!iban) return "";

  const groupLengths = [4, 1, 5, 5, 12];
  const groups = [];
  let offset = 0;

  groupLengths.forEach((length) => {
    const group = iban.slice(offset, offset + length);
    if (group) groups.push(group);
    offset += length;
  });

  const lastGroupIndex = groups.length - 1;
  const completedCurrentGroup =
    lastGroupIndex >= 0 &&
    groups[lastGroupIndex].length === groupLengths[lastGroupIndex];
  const canAcceptAnotherGroup = groups.length < groupLengths.length;

  return `${groups.join("-")}${
    completedCurrentGroup && canAcceptAnotherGroup ? "-" : ""
  }`;
}

export function validItalianIban(value) {
  const iban = normalizeIban(value);
  if (!/^IT\d{2}[A-Z]\d{10}[A-Z0-9]{12}$/.test(iban)) return false;
  let remainder = 0;
  for (const character of iban.slice(4) + iban.slice(0, 4)) {
    const digits = /\d/.test(character)
      ? character
      : String(character.charCodeAt(0) - 55);
    for (const digit of digits)
      remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}

export function bankErrors(cf, abi, cab, iban) {
  const errors = [];
  if (!validFiscalCode(cf))
    errors.push(
      "Codice Fiscale non valido: inserisci 11 cifre o 16 caratteri alfanumerici."
    );
  if (!/^\d{5}$/.test(String(abi)) || abi === "00000")
    errors.push("Inserisci un codice ABI valido di cinque cifre.");
  if (!/^\d{5}$/.test(String(cab)) || cab === "00000")
    errors.push("Inserisci un codice CAB valido di cinque cifre.");
  const normalized = normalizeIban(iban);
  if (normalized && !validItalianIban(normalized))
    errors.push("IBAN non valido: controlla formato e cifre di controllo.");
  else if (
    normalized &&
    (normalized.slice(5, 10) !== abi || normalized.slice(10, 15) !== cab)
  ) {
    errors.push("ABI e CAB non corrispondono ai codici contenuti nellâ€™IBAN.");
  }
  return errors;
}

export function newBankId(usedIds = new Set()) {
  let id;
  do {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    id =
      "f24cb_" +
      Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  } while (usedIds.has(id));
  return id;
}

export function numericPaymentDate(value) {
  const text = String(value || "")
    .trim()
    .replace(/_\d+$/, "");
  let match = text.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (!match) {
    const months = [
      "gennaio",
      "febbraio",
      "marzo",
      "aprile",
      "maggio",
      "giugno",
      "luglio",
      "agosto",
      "settembre",
      "ottobre",
      "novembre",
      "dicembre",
    ];
    const parts = text.toLowerCase().split(/\s+/);
    const month = months.indexOf(parts[1]);
    if (parts.length !== 3 || month < 0) return "";
    match = [
      text,
      parts[0].padStart(2, "0"),
      String(month + 1).padStart(2, "0"),
      parts[2],
    ];
  }
  const [, day, month, year] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (
    date.getFullYear() !== Number(year) ||
    date.getMonth() !== Number(month) - 1 ||
    date.getDate() !== Number(day)
  )
    return "";
  return `${day}-${month}-${year}`;
}

export function bankSoftwareKey(row, software, clients) {
  if (software !== "DATEV")
    return {
      TEAMSYSTEM: "TeamSystem",
      ZUCCHETTI: "Zucchetti",
      SEAC: "Seac",
      SISTEMI: "Sistemi",
    }[software];
  const title = String(row["Cod. Tributo"] || "").replace(
    /^Modello F24(?: semplificato)? - /,
    ""
  );
  const client = (clients || []).find((entry) => entry[1] === title);
  return Number(client?.[2]) === 2 ? "Datev Lib. Prof." : "Datev Aziende";
}

export function bankAccountKey(row, software, clients) {
  return `Cod. Conto AVERE - ${bankSoftwareKey(row, software, clients)}`;
}

export function formatBankAccount(value, software) {
  const digits = String(value || "").replace(/\D/g, "");
  if (software === "TEAMSYSTEM") {
    return digits
      .slice(0, 7)
      .replace(/^(\d{2})(\d)/, "$1/$2")
      .replace(/^(\d{2}\/\d{2})(\d)/, "$1/$2");
  }
  if (software === "SISTEMI") {
    return digits
      .slice(0, 9)
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2}\.\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2}\.\d{2}\.\d{2})(\d)/, "$1.$2");
  }
  return digits.slice(
    0,
    software === "SEAC" ? 6 : software === "DATEV" ? 11 : 30
  );
}

export function validBankAccount(value, software, reservedAccount = "") {
  const account = String(value || "").trim();
  const formats = {
    TEAMSYSTEM: /^\d{2}\/\d{2}\/\d{3}$/,
    DATEV: /^\d{5,11}$/,
    SEAC: /^\d{6}$/,
    SISTEMI: /^\d{2}\.\d{2}\.\d{2}(\.\d{3})?$/,
    ZUCCHETTI: /^\d{5,}$/,
  };
  return Boolean(
    account && account !== reservedAccount && formats[software]?.test(account)
  );
}

export function bankRowFromArray(headers, row) {
  return headers.reduce((record, header, index) => {
    record[header] = row[index];
    return record;
  }, {});
}

function comparableBankAccount(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

function hasMeaningfulBankAccount(value, reservedAccount = "") {
  const text = String(value || "").trim();
  const comparable = comparableBankAccount(text);
  return Boolean(
    text &&
      !["VUOTO", "NA", "N/A", "NESSUNACORRELAZIONE"].includes(comparable) &&
      comparable !== comparableBankAccount(reservedAccount)
  );
}

export function bankAccountConflictErrors(
  rows,
  { software = "", clients = [], reservedAccount = "", columns = null } = {}
) {
  const flatRows = (rows || []).flatMap((row) =>
    Array.isArray(row) ? row : [row]
  );
  const seen = new Map();
  const emitted = new Set();
  const errors = [];

  flatRows.forEach((row, index) => {
    if (!row || typeof row !== "object") return;
    const iban = normalizeIban(row.IBAN);
    if (!iban) return;
    const accountColumns =
      columns ||
      (software
        ? [bankAccountKey(row, software, clients)]
        : BANK_ACCOUNT_COLUMNS);
    accountColumns.forEach((column) => {
      const account = row[column];
      if (!hasMeaningfulBankAccount(account, reservedAccount)) return;
      const comparable = comparableBankAccount(account);
      const key = `${iban}::${column}`;
      const previous = seen.get(key);
      if (previous && previous.comparable !== comparable) {
        const conflictKey = `${key}::${previous.comparable}::${comparable}`;
        if (emitted.has(conflictKey)) return;
        emitted.add(conflictKey);
        errors.push(
          `Lo stesso IBAN ${iban} non può avere codici conto diversi in "${column}" ` +
            `(righe ${previous.index + 1} e ${index + 1}).`
        );
        return;
      }
      if (!previous) seen.set(key, { comparable, index });
    });
  });

  return errors;
}

export function bankGroupErrors(
  groups,
  software,
  clients,
  reservedAccount = ""
) {
  const rowErrors = groups.flatMap((group, index) => {
    if (group.length !== 1)
      return [`Pagamento ${index + 1}: seleziona unâ€™associazione bancaria.`];
    const row = group[0];
    const [cf = "", abi = "", cab = ""] = String(
      row["Codice Fiscale-ABI-CAB"] || ""
    ).split("-");
    const errors = bankErrors(cf, abi, cab, row.IBAN);
    const account = String(
      row[bankAccountKey(row, software, clients)] || ""
    ).trim();
    if (!validBankAccount(account, software, reservedAccount))
      errors.push("Inserisci un codice conto valido.");
    const date = numericPaymentDate(row.Data);
    if (!date || date === "01-01-1970")
      errors.push("Conferma la data di pagamento in fase 1.");
    return errors.map((error) => `Pagamento ${index + 1}: ${error}`);
  });
  return [
    ...rowErrors,
    ...bankAccountConflictErrors(groups, {
      software,
      clients,
      reservedAccount,
    }).map((error) => `Coordinate bancarie: ${error}`),
  ];
}

export function initialBankSelection(candidates) {
  const used = new Set(
    candidates
      .flat()
      .map((row) => row["ID Associazione"])
      .filter(Boolean)
  );
  return candidates.map((group) => {
    if (group.length !== 1) return [];
    const row = { ...group[0] };
    if (!row["ID Associazione"]) {
      row["ID Associazione"] = newBankId(used);
      used.add(row["ID Associazione"]);
    }
    return [row];
  });
}
